import React, { createContext, useContext, type ReactNode } from 'react'
import pako from 'pako'
import { type SAML2SSOConfig } from '../../config/ssoTypes'
import { SAML_TEST_CONFIG } from '../../config/domainSSOConfig'

interface SAML2ContextType {
  initiateLogin: () => void
  config: SAML2SSOConfig
}

const SAML2Context = createContext<SAML2ContextType | undefined>(undefined)

interface SAML2ProviderProps {
  config: SAML2SSOConfig
  children: ReactNode
}

/**
 * Check if SAML test mode is enabled via URL parameter
 * Returns 'production', 'local', or null
 */
const getTestMode = (): 'production' | 'local' | null => {
  const urlParams = new URLSearchParams(window.location.search)
  const testSaml = urlParams.get('test_saml')
  
  // Block in production
  if (process.env.NODE_ENV === 'production') {
    if (testSaml !== null) {
      console.error('SECURITY: SAML test mode blocked in production')
    }
    return null
  }
  
  if (!SAML_TEST_CONFIG.enabled) {
    return null
  }
  
  // test_saml=local -> Use local Lambda
  if (testSaml === 'local') {
    return 'local'
  }
  
  // test_saml=true -> Use production Lambda (default)
  if (testSaml === 'true') {
    return 'production'
  }
  
  return null
}

/**
 * Submit mock SAML response to Lambda endpoint
 * @param lambdaUrl - Either production or local Lambda URL
 * @param mode - 'production' or 'local' for logging
 */
const submitToLambda = (lambdaUrl: string, mode: 'production' | 'local'): void => {
  const displayMode = mode === 'production' ? 'Production Lambda' : 'Local Lambda'
  
  console.warn(`🧪 DEV MODE: Submitting to ${displayMode}`)
  console.log('Lambda URL:', lambdaUrl)
  console.log('RelayState:', SAML_TEST_CONFIG.getRelayState())
  console.log('Test User: shaneblaser@gmail.com (Shane Blaser)')
  
  // Create a form to POST to the Lambda endpoint
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = lambdaUrl
  form.style.display = 'none'
  
  // Add SAMLResponse field
  const samlResponseInput = document.createElement('input')
  samlResponseInput.type = 'hidden'
  samlResponseInput.name = 'SAMLResponse'
  samlResponseInput.value = SAML_TEST_CONFIG.mockSAMLResponse
  form.appendChild(samlResponseInput)
  
  // Add RelayState field (where to redirect after auth)
  const relayStateInput = document.createElement('input')
  relayStateInput.type = 'hidden'
  relayStateInput.name = 'RelayState'
  relayStateInput.value = SAML_TEST_CONFIG.getRelayState()
  form.appendChild(relayStateInput)
  
  // Submit the form
  document.body.appendChild(form)
  form.submit()
}

export const SAML2Provider: React.FC<SAML2ProviderProps> = ({ config, children }) => {
  const initiateLogin = (): void => {
    // Check for test mode
    const testMode = getTestMode()
    
    if (testMode === 'production') {
      // Use production Lambda
      submitToLambda(SAML_TEST_CONFIG.productionLambdaUrl, 'production')
      return
    }
    
    if (testMode === 'local') {
      // Use local Lambda test server
      submitToLambda(SAML_TEST_CONFIG.localLambdaUrl, 'local')
      return
    }
    
    // Normal flow - Build SAML request and redirect to IdP
    const samlRequest = buildSAMLRequest(config)
    window.location.href = `${config.idpUrl}?SAMLRequest=${encodeURIComponent(samlRequest)}`
  }

  return (
    <SAML2Context.Provider value={{ initiateLogin, config }}>
      {children}
    </SAML2Context.Provider>
  )
}

export const useSAML2 = (): SAML2ContextType => {
  const context = useContext(SAML2Context)
  if (context === undefined) {
    throw new Error('useSAML2 must be used within SAML2Provider')
  }
  return context
}

/**
 * Build and encode SAML AuthnRequest according to SAML 2.0 HTTP-Redirect binding
 * 
 * Process:
 * 1. Build SAML XML string
 * 2. DEFLATE (compress) the XML
 * 3. Base64 encode the compressed data
 * 4. URL encode for GET parameter
 * 
 * @param config - SAML2 SSO configuration
 * @returns Base64-encoded, deflated SAML request ready for URL parameter
 */
const buildSAMLRequest = (config: SAML2SSOConfig): string => {
  try {
    // Step 1: Build SAML XML
    const samlRequest = `
    <samlp:AuthnRequest
      xmlns:samlp="urn:oasis:names:tc:SAML:2.0:protocol"
      xmlns:saml="urn:oasis:names:tc:SAML:2.0:assertion"
      ID="_${generateId()}"
      Version="2.0"
      IssueInstant="${new Date().toISOString()}"
      AssertionConsumerServiceURL="${config.acsUrl}"
      Destination="${config.idpUrl}">
      <saml:Issuer>${config.entityId}</saml:Issuer>
    </samlp:AuthnRequest>
  `.trim()
    
    // Step 2: DEFLATE compression (required by SAML 2.0 HTTP-Redirect binding)
    // This is what Shibboleth expects and why we were getting "Stale Request" error
    const deflated = pako.deflate(samlRequest)
    
    // Step 3: Convert Uint8Array to binary string
    let binaryString = ''
    const len = deflated.byteLength
    for (let i = 0; i < len; i++) {
      binaryString += String.fromCharCode(deflated[i])
    }
    
    // Step 4: Base64 encode
    const base64Encoded = btoa(binaryString)
    
    console.log('✅ SAML Request built successfully (deflated + base64)')
    console.log('Request ID:', samlRequest.match(/ID="([^"]+)"/)?.[1])
    console.log('Original size:', samlRequest.length, 'bytes')
    console.log('Compressed size:', deflated.length, 'bytes')
    console.log('Compression ratio:', Math.round((1 - deflated.length / samlRequest.length) * 100) + '%')
    
    return base64Encoded
    
  } catch (error) {
    console.error('❌ Error building SAML request:', error)
    
    // In development, provide helpful error message
    if (process.env.NODE_ENV !== 'production') {
      console.error('DEFLATE compression failed. This will cause authentication to fail.')
      console.error('Ensure pako library is installed: npm install pako')
    }
    
    throw new Error('Failed to build SAML request: ' + (error instanceof Error ? error.message : 'Unknown error'))
  }
}

const generateId = (): string => {
  return 'id' + Math.random().toString(36).substring(2, 9)
}

